import { Image } from 'expo-image';
import { ReactNode, forwardRef, useEffect, useMemo, useRef } from 'react';
import type { ImageStyle, ViewStyle } from 'react-native';
import { Animated, Easing, Platform, Image as RNImage, StyleSheet, View } from 'react-native';

import { formatCentsToDollars } from '@fhs/menu/src/utils';

import { createMqStyles } from '../../mq-styles';
import { tokens } from '../../tokens';
import type { ImageAssetWithAltText } from '../../types';
import { Pressable, PressableProps, usePressableState } from '../pressable';
import { XStack } from '../stack';
import { Text } from '../text';

import type { BadgeType, TileSizeType } from './types';

const AnimatedImage = Animated.createAnimatedComponent(Image);

/** Web: send pointer/hover hit-testing to the parent Pressable so VoiceOver targets the labeled control, not inner text/image. */
const webPointerPassthrough =
  Platform.OS === 'web' ? ({ pointerEvents: 'none' } as const) : {};

export type ProductListTileProps = PressableProps & {
  imageRecyclingKey?: string;
  size: TileSizeType;
  image: ImageAssetWithAltText;
  slug?: string;
  isAvailable: boolean;
  badge?: BadgeType;
  title: string;
  description: ReactNode;
  showPriceAndCalories?: boolean;
  calories?: number;
  priceCents?: number;
};

export const ProductListTile = forwardRef<View, ProductListTileProps>(function ProductListTile(
  {
    imageRecyclingKey,
    showPriceAndCalories = false,
    size,
    image,
    isAvailable,
    badge,
    title,
    description,
    priceCents = 0,
    calories = 0,
    ...pressableProps
  },
  forwardedRef
) {
  const stackStyles = useStackStyles();

  const tileAccessibilityLabel = useMemo(() => {
    const parts: string[] = [];
    if (!isAvailable) {
      parts.push('Out of stock');
    }
    if (badge === 'NEW') {
      parts.push('New item');
    }
    if (badge === 'LIMITED') {
      parts.push('Limited time');
    }
    parts.push(title);
    if (typeof description === 'string' && description.trim()) {
      parts.push(description.trim());
    }
    if (showPriceAndCalories) {
      if (priceCents) {
        parts.push(`+${formatCentsToDollars(priceCents)}`);
      }
      if (calories) {
        parts.push(`${calories} Cal`);
      }
    }
    return parts.join(', ');
  }, [title, description, showPriceAndCalories, priceCents, calories, badge, isAvailable]);

  return (
    <Pressable
      {...pressableProps}
      ref={forwardedRef}
      accessibilityLabel={tileAccessibilityLabel}
      disabled={!isAvailable || pressableProps.disabled}
      style={
        // Have to use StyleSheet.flatten manually due to issue with Link component's asChild functionality
        // circumventing react-native-web's automatic flattening for the style prop
        useMemo(
          () =>
            StyleSheet.flatten([
              stackStyles.stack as unknown as ViewStyle,
              stackStyles[size] as unknown as ViewStyle,
            ]),
          [size, stackStyles]
        )
      }
      disabledStyle={pressableProps.disabledStyle}
      outlineStyle={[pressableStyle.pressableNoOutline, pressableProps.outlineStyle]}
      outlineHoveredStyle={[pressableStyle.pressableNoOutline, pressableProps.outlineHoveredStyle]}
      outlineDisabledStyle={[
        pressableStyle.pressableNoOutline,
        pressableProps.outlineDisabledStyle,
      ]}
      outlinePressedStyle={[pressableStyle.pressableNoOutline, pressableProps.outlinePressedStyle]}
      outlineFocusedStyle={[pressableStyle.pressableNoOutline, pressableProps.outlineFocusedStyle]}
    >
      <OutlineBox size={size} />
      <ProductListTileImage
        recyclingKey={imageRecyclingKey}
        image={image}
        isAvailable={isAvailable}
        size={size}
        title={title}
      />
      <Badge type={!isAvailable ? 'OUT_OF_STOCK' : badge} />
      <TileHeading title={title} />
      <Text.Paragraph
        accessible={false}
        importantForAccessibility="no-hide-descendants"
        size="sm"
        numberOfLines={2}
        style={[descriptionStyles.description, !isAvailable && descriptionStyles.disabled]}
        // Enables ellipsis
        selectable={false}
        {...webPointerPassthrough}
      >
        {description}
      </Text.Paragraph>
      {showPriceAndCalories && (
        <XStack
          accessible={false}
          accessibilityElementsHidden
          importantForAccessibility="no-hide-descendants"
          style={descriptionStyles.priceAndCalories}
          {...webPointerPassthrough}
        >
          {priceCents ? (
            <Text.Ui
              size="sm"
              weight="semibold"
              style={[!isAvailable && descriptionStyles.disabled]}
              selectable={false}
              {...webPointerPassthrough}
            >
              +{formatCentsToDollars(priceCents)}
            </Text.Ui>
          ) : null}
          {priceCents && calories ? (
            <Text.Ui size="sm" weight="semibold" {...webPointerPassthrough}>
              •
            </Text.Ui>
          ) : null}
          {calories ? (
            <Text.Ui
              size="sm"
              style={[!isAvailable && descriptionStyles.disabled]}
              selectable={false}
              {...webPointerPassthrough}
            >
              {calories} Cal
            </Text.Ui>
          ) : null}
        </XStack>
      )}
    </Pressable>
  );
});

function ProductListTileImage(props: {
  recyclingKey?: string;
  image: ImageAssetWithAltText;
  isAvailable: boolean;
  size: TileSizeType;
  title: string;
}) {
  const pressableState = usePressableState();
  const isEmphasized = pressableState.hovered;
  const scaleRef = useRef(new Animated.Value(isEmphasized ? 1 : 0));

  useEffect(() => {
    if (Platform.OS === 'web') return;
    Animated.timing(scaleRef.current, {
      toValue: isEmphasized ? 1 : 0,
      easing: Easing.out(Easing.ease),
      duration: 300,
      useNativeDriver: true,
    }).start();
  }, [isEmphasized]);

  const titleForAlt =
    typeof props.title === 'string' ? props.title : String(props.title ?? '');
  const imageAlt =
    props.image.altText?.trim() || titleForAlt.trim() || 'Menu item';

  const imageStyle = [
    imageStyles.img,
    imageStyles[props.size],
    !props.isAvailable && imageStyles.disabled,
  ];

  const uri = props.image.asset?.uri;
  const imageSource = uri ? { uri } : null;
  const placeholder = props.image.asset?.blurHash
    ? { blurhash: props.image.asset.blurHash }
    : undefined;

  // Web: `expo-image` inside transformed views has been unreliable in RN Web (blank tiles). Use
  // react-native's Image (real <img>) for remote menu thumbnails; keep expo-image on native.
  if (Platform.OS === 'web') {
    const flatImageStyle = StyleSheet.flatten(imageStyle) as ImageStyle;
    return (
      <View
        style={
          {
            width: '100%',
            minHeight: props.size === 'lg' ? 90 : 70,
            alignItems: 'center',
            justifyContent: 'flex-start',
            transformOrigin: 'bottom',
            transform: [{ scale: isEmphasized ? 1.15 : 1 }],
          } as ViewStyle
        }
      >
        {uri ? (
          <RNImage
            source={{ uri }}
            accessibilityLabel={imageAlt}
            resizeMode="contain"
            style={flatImageStyle}
            {...webPointerPassthrough}
          />
        ) : null}
      </View>
    );
  }

  return (
    <AnimatedImage
      recyclingKey={props.recyclingKey ?? uri}
      source={imageSource}
      placeholder={placeholder}
      alt={imageAlt}
      accessibilityLabel={imageAlt}
      accessible={false}
      importantForAccessibility="no"
      accessibilityElementsHidden
      {...webPointerPassthrough}
      contentFit="contain"
      style={imageStyle}
    />
  );
}

export type MenuItem = {
  id: string;
  slug: string;
  url: string;
  image: ImageAssetWithAltText;
  displayName: string;
  descriptionShort: string;
  items: any[];
};

const badgeTypeToText: Record<BadgeType, string> = {
  NONE: '',
  NEW: 'New!',
  LIMITED: 'Limited!',
  OUT_OF_STOCK: 'Out of Stock',
};

function Badge(props: { type?: BadgeType }) {
  const badgeType = props.type ?? 'NONE';

  if (badgeType === 'NONE') {
    return null;
  }

  return (
    <View
      accessible={false}
      accessibilityElementsHidden
      {...webPointerPassthrough}
      style={[badgeStyle.badge, badgeStyle[`badge-${badgeType}`]]}
    >
      <Text.Ui
        size="sm"
        weight="semibold"
        style={[badgeStyle.text, badgeStyle[`text-${badgeType}`]]}
        {...webPointerPassthrough}
      >
        {badgeTypeToText[badgeType]}
      </Text.Ui>
    </View>
  );
}

function OutlineBox(props: Pick<ProductListTileProps, 'size'>) {
  const pressableState = usePressableState();

  return (
    <View
      accessible={false}
      accessibilityElementsHidden
      {...webPointerPassthrough}
      style={[
        boxStyles.box,
        boxStyles[props.size],
        pressableState.hovered && boxStyles.hovered,
        pressableState.focused && boxStyles.focused,
      ]}
    />
  );
}

function TileHeading(props: Pick<ProductListTileProps, 'title'>) {
  const pressableState = usePressableState();

  return (
    <View
      accessible={false}
      accessibilityElementsHidden
      importantForAccessibility="no-hide-descendants"
      {...webPointerPassthrough}
      style={headingStyles.headingContainer}
    >
      <Text.Heading
        numberOfLines={2}
        type="four"
        // Enables ellipsis
        selectable={false}
        {...webPointerPassthrough}
        style={[
          headingStyles.heading,
          pressableState.hovered && headingStyles.hovered,
          pressableState.focused && headingStyles.focused,
          pressableState.disabled && headingStyles.disabled,
        ]}
      >
        {props.title}
      </Text.Heading>
    </View>
  );
}

const badgeStyle = StyleSheet.create({
  badge: {
    paddingHorizontal: 8,
    position: 'absolute',
    top: 0,
    right: 0,
    backgroundColor: tokens.colors.$success,
    height: 24,
    justifyContent: 'center',
    borderRadius: 4,
  },
  'badge-OUT_OF_STOCK': {
    backgroundColor: tokens.colors.$houseLight,
  },
  text: {
    color: tokens.colors.$white,
  },
  'text-OUT_OF_STOCK': {
    color: tokens.colors.$houseDark,
  },
});

const pressableStyle = StyleSheet.create({
  pressableNoOutline: {
    borderColor: 'transparent',
  },
});

const useStackStyles = createMqStyles({
  stack: {
    $base: {
      flexShrink: 1,
      flexGrow: 1,
      gap: 4,
      position: 'relative',
      backgroundColor: 'white',
      alignItems: 'center',
      padding: 16,
      paddingTop: 0,
    },

    $gteDesktop: {
      padding: 24,
      paddingTop: 0,
    },
  },
  sm: {
    $base: {},
    $gteDesktop: {
      paddingBottom: 12,
    },
  },
  lg: {
    $base: {
      paddingBottom: 20,
    },
  },
});

const boxStyles = StyleSheet.create({
  box: {
    borderRadius: 8,
    borderColor: tokens.colors.$blackOpacity10,
    borderWidth: 1,
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
  },
  lg: {
    top: 52,
  },
  sm: {
    top: 32,
  },
  hovered: {
    borderColor: tokens.colors.$houseRedOpacity30,
  },
  focused: {
    borderColor: tokens.colors.$houseRedOpacity30,
  },
});

const headingStyles = StyleSheet.create({
  headingContainer: {
    minHeight: 40,
    alignItems: 'center',
    justifyContent: 'center',
    maxWidth: '100%',
  },
  heading: {
    textAlign: 'center',
  },
  hovered: {
    color: tokens.colors.$houseRedDarken,
  },
  focused: {
    color: tokens.colors.$houseRedDarken,
  },
  disabled: {
    color: tokens.colors.$disabledText,
  },
});

const descriptionStyles = StyleSheet.create({
  description: {
    textAlign: 'center',
    minHeight: 32,
  },
  disabled: {
    color: tokens.colors.$disabledText,
  },
  priceAndCalories: {
    gap: 4,
    marginTop: 8,
  },
});

const imageStyles = StyleSheet.create({
  img: {
    width: '100%',
  },
  disabled: {
    opacity: 0.74,
  },
  lg: {
    height: 90,
  },
  sm: {
    height: 70,
  },
});
